import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../dashboard/components/Navbar';
import Sidebar from '../dashboard/components/Sidebar';
import { requisicionesService } from '../../services/requisiciones.service';

interface Requisicion {
    id: string;
    numero: string;
    fecha: string;
    solicitante: string;
    departamento: string;
    items: number;
    total: number;
    estado: string;
}

export default function SeleccionRequisicionPage() {
    const navigate = useNavigate();
    const [requisiciones, setRequisiciones] = useState<Requisicion[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadRequisiciones();
    }, []);

    const loadRequisiciones = async () => {
        try {
            // In a real app, we would filter by 'Aprobada' status in the API
            const data = await requisicionesService.getAll({ estado: 'Aprobada' });
            const list = Array.isArray(data) ? data : (data as any).data || [];
            // Filter locally just in case API returns all
            setRequisiciones(list.filter((r: Requisicion) => r.estado === 'Aprobada'));
        } catch (error) {
            console.error('Error loading requisiciones:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (requisicion: Requisicion) => {
        navigate('/compras/cotizaciones/nueva', { state: { requisicion } });
    };

    const filteredRequisiciones = requisiciones.filter(req =>
        req.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.solicitante.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.departamento.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState<any[]>([]);
    const [selectedReqForDetail, setSelectedReqForDetail] = useState<Requisicion | null>(null);

    const handleViewDetail = async (req: Requisicion) => {
        try {
            setSelectedReqForDetail(req);
            const details = await requisicionesService.getDetalle(req.id);
            setSelectedDetail(details);
            setShowDetailModal(true);
        } catch (error) {
            console.error('Error loading details:', error);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />
            <div className="flex">
                <Sidebar />
                <main className="flex-1 p-8 pt-[105px] transition-all duration-300" style={{ marginLeft: 'var(--sidebar-width, 256px)' }}>
                    <div className="max-w-6xl mx-auto">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">Seleccionar Requisición</h1>
                                <p className="text-slate-600">Elija una requisición aprobada para generar la cotización</p>
                            </div>
                            <button
                                onClick={() => navigate('/compras')}
                                className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                Cancelar
                            </button>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                            <div className="relative max-w-md mb-6">
                                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
                                <input
                                    type="text"
                                    placeholder="Buscar por número, solicitante..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Número</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Solicitante</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Departamento</th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Items</th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Total Estimado</th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {loading ? (
                                            <tr><td colSpan={7} className="px-6 py-8 text-center">Cargando...</td></tr>
                                        ) : filteredRequisiciones.length === 0 ? (
                                            <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500">No hay requisiciones aprobadas disponibles</td></tr>
                                        ) : (
                                            filteredRequisiciones.map((req) => (
                                                <tr key={req.id} className="hover:bg-slate-50 transition-colors group">
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-800">{req.numero}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{req.fecha}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{req.solicitante}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{req.departamento}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">{req.items}</td>
                                                    <td className="px-6 py-4 text-sm font-medium text-slate-800 text-right">
                                                        ${req.total.toLocaleString()}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => handleViewDetail(req)}
                                                                className="w-8 h-8 flex items-center justify-center text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Ver Detalle"
                                                            >
                                                                <i className="ri-eye-line text-lg"></i>
                                                            </button>
                                                            <button
                                                                onClick={() => handleSelect(req)}
                                                                className="px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-100 rounded-lg hover:bg-teal-200 transition-colors"
                                                            >
                                                                Seleccionar
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Detail Modal */}
                    {showDetailModal && selectedReqForDetail && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                                <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                                    <div>
                                        <h2 className="text-xl font-bold text-slate-800">Detalle de Requisición</h2>
                                        <p className="text-sm text-slate-500">{selectedReqForDetail.numero} - {selectedReqForDetail.solicitante}</p>
                                    </div>
                                    <button
                                        onClick={() => setShowDetailModal(false)}
                                        className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                    >
                                        <i className="ri-close-line text-xl"></i>
                                    </button>
                                </div>
                                <div className="p-6">
                                    <table className="w-full">
                                        <thead className="bg-slate-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Producto</th>
                                                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Cantidad</th>
                                                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Precio Est.</th>
                                                <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Subtotal</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {selectedDetail.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="px-4 py-3 text-sm text-slate-800">{item.nombre_medicamento || item.medicamento_id}</td>
                                                    <td className="px-4 py-3 text-sm text-slate-600 text-right">{item.cantidad}</td>
                                                    <td className="px-4 py-3 text-sm text-slate-600 text-right">${item.precio_estimado?.toLocaleString()}</td>
                                                    <td className="px-4 py-3 text-sm font-medium text-slate-800 text-right">${item.subtotal?.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-slate-50 border-t border-slate-200">
                                            <tr>
                                                <td colSpan={3} className="px-4 py-3 text-right font-bold text-slate-700">Total Estimado:</td>
                                                <td className="px-4 py-3 text-right font-bold text-teal-600">${selectedReqForDetail.total.toLocaleString()}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="p-4 bg-slate-50 border-t border-slate-200 rounded-b-xl flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowDetailModal(false)}
                                        className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                                    >
                                        Cerrar
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            handleSelect(selectedReqForDetail);
                                        }}
                                        className="px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors"
                                    >
                                        Usar esta Requisición
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
